// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * DSA 1.8 vector layers: which drawn feature is at a place, and what its
 * inspection says (notes §8).
 *
 * `pickVectorAt` works in lon/lat on what the last frame drew, so the
 * headset and a phone's AR globe (three.js, no MapLibre to ask) pick the
 * same way: a point under its disc or within reach of it, a line or track
 * within reach of its drawn path, a polygon containing the place. The
 * topmost wins: the last layer in `order`, then the last drawn feature.
 * The browser globe asks MapLibre instead (vectorMapLayer.pick).
 *
 * `inspectFeature` is the panel's text: the localized `title` template,
 * the listed `fields` with their labels and units, and the `link`.
 */
import { kmPerDegOf, type CompiledVectorLayer, type LonLat, type VectorFeature } from './vectorModel'
import { cutTrack, subdivide, unwrap } from './vectorGeo'
import { attrsAt } from './vectorLabels'
import { evalNumber, fillTemplate, formatCell, localize, shownUnits } from './vectorStyle'
import type { VectorDrawItem } from './vectorTimeline'

const RAD = Math.PI / 180

/** One layer as the last frame drew it. */
export interface VectorFrameLayer {
  layer: CompiledVectorLayer
  items: VectorDrawItem[]
}

export interface VectorPick {
  layer: CompiledVectorLayer
  feature: VectorFeature
}

/** Great-circle distance in degrees. */
export function arcDeg(a: LonLat, b: LonLat): number {
  const [l1, p1] = [a[0] * RAD, a[1] * RAD]
  const [l2, p2] = [b[0] * RAD, b[1] * RAD]
  const h = Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin((l2 - l1) / 2) ** 2
  return (2 * Math.asin(Math.min(1, Math.sqrt(h)))) / RAD
}

/**
 * Degrees from `at` to the nearest point of a path, measured flat around
 * `at` (fine for the short reach of a tap). The path is made continuous,
 * then each segment is moved whole by 360° to the copy nearest the tap,
 * so a path across ±180 measures right.
 */
function pathDeg(at: LonLat, path: LonLat[]): number {
  if (!path.length) return Infinity
  const k = Math.cos(at[1] * RAD)
  const p = unwrap(path)
  const shift = (lon: number) => 360 * Math.round((lon - at[0]) / 360)
  if (p.length === 1) return Math.hypot((p[0][0] - shift(p[0][0]) - at[0]) * k, p[0][1] - at[1])
  let best = Infinity
  for (let i = 1; i < p.length; i++) {
    const off = shift((p[i - 1][0] + p[i][0]) / 2)
    const bx = (p[i - 1][0] - off - at[0]) * k, by = p[i - 1][1] - at[1]
    const ax = (p[i][0] - off - at[0]) * k, ay = p[i][1] - at[1]
    const dx = ax - bx, dy = ay - by
    const len2 = dx * dx + dy * dy
    const t = len2 > 0 ? Math.max(0, Math.min(1, -(bx * dx + by * dy) / len2)) : 0
    best = Math.min(best, Math.hypot(bx + t * dx, by + t * dy))
  }
  return best
}

/**
 * Even-odd test in lon/lat, the ring moved whole by 360° to the copy
 * nearest the place. Rings are never unwrapped: §4 splits them at the
 * antimeridian, and a polar cap is closed along ±180 and the pole.
 */
function inRing(at: LonLat, ring: LonLat[]): boolean {
  let inside = false
  const u = ring
  const mid = u.reduce((sum, v) => sum + v[0], 0) / Math.max(1, u.length)
  const off = 360 * Math.round((mid - at[0]) / 360)
  const r = u.map(([lon, lat]): LonLat => [lon - off, lat])
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [xi, yi] = r[i], [xj, yj] = r[j]
    if ((yi > at[1]) !== (yj > at[1]) && at[0] < ((xj - xi) * (at[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/** A point's drawn radius in degrees (0 for a px size: only the reach counts). */
function pointRadiusDeg(layer: CompiledVectorLayer, item: VectorDrawItem): number {
  const size = layer.spec.style.size
  if (!size || size.units === 'px') return 0
  const d = evalNumber(size.value, item.feature.attrs, 0) ?? 0
  return ((size.units === 'km' ? d / kmPerDegOf(layer) : d) * item.size) / 2
}

function hits(layer: CompiledVectorLayer, item: VectorDrawItem, at: LonLat, reachDeg: number): boolean {
  const f = item.feature
  if (item.opacity <= 0.05) return false
  switch (layer.spec.geometry) {
    case 'point':
      return !!f.point && arcDeg(at, f.point) <= pointRadiusDeg(layer, item) + reachDeg
    case 'line':
      return (f.parts ?? []).some((p) => pathDeg(at, subdivide(unwrap(p))) <= reachDeg)
    case 'track': {
      if (!f.parts?.[0] || !f.vertexTimes) return false
      const track = layer.spec.style.track
      const window = track?.trail?.mode === 'window' ? track.trail.dataSeconds : undefined
      const cut = cutTrack(unwrap(f.parts[0]), f.vertexTimes, item.headT, track?.interpolate !== false, window)
      return pathDeg(at, cut.head ? [...cut.path, cut.head] : cut.path) <= reachDeg
    }
    case 'polygon':
      return (f.polygons ?? []).some((rings) => rings.length > 0 && inRing(at, rings[0]) && !rings.slice(1).some((h) => inRing(at, h)))
        || (f.polygons ?? []).some((rings) => rings.some((r) => pathDeg(at, r) <= reachDeg))
  }
}

/**
 * The topmost drawn, pickable feature at a place: the last layer, then the
 * last item (items are drawn in zOrder, the last on top).
 */
export function pickVectorAt(frame: readonly VectorFrameLayer[], lat: number, lon: number, reachDeg: number): VectorPick | null {
  const at: LonLat = [lon, lat]
  for (let l = frame.length - 1; l >= 0; l--) {
    const { layer, items } = frame[l]
    if (layer.spec.interaction?.pickable === false) continue
    for (let i = items.length - 1; i >= 0; i--) {
      if (hits(layer, items[i], at, reachDeg)) return { layer, feature: items[i].feature }
    }
  }
  return null
}

export interface VectorInspection {
  title: string
  rows: Array<{ label: string; value: string }>
  /** An http(s) link, or null. */
  link: string | null
}

/**
 * §8: what the inspection panel shows for a feature, in the viewer's
 * language. `at` (data time, Unix s) reads a track's per-vertex values
 * where its head is drawn (the wind now, not at its first fix).
 */
export function inspectFeature(layer: CompiledVectorLayer, feature: VectorFeature, lang: string, at?: number): VectorInspection {
  const fields = layer.spec.fields
  const interaction = layer.spec.interaction
  const attrs = at === undefined ? feature.attrs : attrsAt(feature, at)
  const titleTemplate = localize(interaction?.title, lang)
  const title = titleTemplate
    ? fillTemplate(titleTemplate, attrs, fields, lang, layer.epochMs, 'text').trim()
    : layer.name
  const names = interaction?.fields ?? fields.filter((f) => f.role === 'attribute').map((f) => f.name)
  const rows: VectorInspection['rows'] = []
  for (const name of names) {
    const field = fields.find((f) => f.name === name)
    const value = formatCell(field, attrs[name], lang, layer.epochMs)
    if (!value) continue
    // Times read as dates, so only an attribute carries its units.
    const units = field?.role === 'attribute' && shownUnits(field) ? ` ${shownUnits(field)}` : ''
    rows.push({ label: localize(field?.label, lang) || name, value: `${value}${units}` })
  }
  let link: string | null = null
  if (interaction?.link) {
    const filled = fillTemplate(interaction.link, attrs, fields, lang, layer.epochMs, 'link')
    try {
      const url = new URL(filled)
      if (url.protocol === 'https:' || url.protocol === 'http:') link = url.toString()
    } catch { /* not a link */ }
  }
  return { title, rows, link }
}
